(() => {
  const MANAGER_KEY = 'EcxShowcaseStudio';

  const FORMAT_LABELS = {
    square: 'Cuadrado · 1:1',
    portrait: 'Instagram · 4:5',
    story: 'Historia · 9:16',
    landscape: 'Horizontal · 16:9',
  };

  const STATUS_LABELS = {
    available: 'Disponible',
    preorder: 'Bajo pedido',
    low_stock: 'Últimas unidades',
    sale: 'Oferta',
    new: 'Nuevo',
  };

  const cleanText = (value = '') => String(value).replace(/\s+/g, ' ').trim();

  const stripHtml = (value = '') => {
    const source = String(value)
      .replace(/<\s*br\s*\/?\s*>/gi, '\n')
      .replace(/<\/(p|li|div|h[1-6])>/gi, '\n')
      .replace(/<li[^>]*>/gi, '- ');
    const element = document.createElement('div');
    element.innerHTML = source;
    return element.textContent || element.innerText || '';
  };

  const truncateAtWord = (value, max = 58) => {
    const text = cleanText(value);
    if (text.length <= max) return text;
    const shortened = text.slice(0, max - 1).replace(/\s+\S*$/, '');
    return `${shortened || text.slice(0, max - 1)}…`;
  };

  const imageSource = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    return value.src || value.url || value.originalSrc || value.preview_image?.src || '';
  };

  const normalizeTags = (tags) => {
    if (Array.isArray(tags)) return tags.map(cleanText).filter(Boolean);
    if (typeof tags === 'string') return tags.split(',').map(cleanText).filter(Boolean);
    return [];
  };

  const imageKey = (value) => {
    try {
      const url = new URL(imageSource(value), window.location.origin);
      url.searchParams.delete('width');
      return `${url.origin}${url.pathname}`;
    } catch (_error) {
      return imageSource(value).split('?')[0];
    }
  };

  const sizedImageUrl = (value, width) => {
    const source = imageSource(value);
    if (!source) return '';
    try {
      const url = new URL(source, window.location.origin);
      if (/cdn\.shopify\.com$/i.test(url.hostname) || /shopifycdn\.net$/i.test(url.hostname)) {
        url.searchParams.set('width', String(width));
      }
      return url.toString();
    } catch (_error) {
      return source;
    }
  };

  const debounce = (callback, delay = 260) => {
    let timer;
    return (...args) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => callback(...args), delay);
    };
  };

  class ShowcaseStudio {
    constructor(root) {
      this.root = root;
      this.canvas = root.querySelector('[data-ecx-canvas]');
      this.initialProductNode = root.querySelector('[data-ecx-initial-product]');
      this.abortController = null;
      this.scrollPosition = { x: 0, y: 0 };
      this.state = {
        product: null,
        variant: null,
        imageIndex: 0,
        autoFeatures: true,
        capture: false,
      };

      this.refs = {
        search: root.querySelector('[data-ecx-product-search]'),
        searchBox: root.querySelector('.ecx-search'),
        searchResults: root.querySelector('[data-ecx-search-results]'),
        searchStatus: root.querySelector('[data-ecx-search-status]'),
        variant: root.querySelector('[data-ecx-variant]'),
        status: root.querySelector('[data-ecx-status]'),
        imagesGroup: root.querySelector('[data-ecx-images-group]'),
        imagePicker: root.querySelector('[data-ecx-image-picker]'),
        layout: root.querySelector('[data-ecx-layout]'),
        background: root.querySelector('[data-ecx-background]'),
        formatButtons: [...root.querySelectorAll('[data-ecx-format]')],
        formatLabel: root.querySelector('[data-ecx-format-label]'),
        promoInput: root.querySelector('[data-ecx-promo-input]'),
        secondaryInput: root.querySelector('[data-ecx-secondary-input]'),
        featureInputs: [...root.querySelectorAll('[data-ecx-feature-input]')],
        toggles: [...root.querySelectorAll('[data-ecx-toggle]')],
        reset: root.querySelector('[data-ecx-reset]'),
        capture: root.querySelector('[data-ecx-capture]'),
        captureExit: root.querySelector('[data-ecx-capture-exit]'),
        productImageShell: root.querySelector('.ecx-product-image-shell'),
        title: root.querySelector('[data-ecx-product-title]'),
        vendor: root.querySelector('[data-ecx-product-vendor]'),
        secondary: root.querySelector('[data-ecx-product-secondary]'),
        price: root.querySelector('[data-ecx-price]'),
        compare: root.querySelector('[data-ecx-compare]'),
        promo: root.querySelector('[data-ecx-promo]'),
        features: root.querySelector('[data-ecx-features]'),
        skuWrap: root.querySelector('[data-ecx-sku-wrap]'),
        sku: root.querySelector('[data-ecx-sku]'),
        availability: root.querySelector('[data-ecx-availability]'),
        statusBadge: root.querySelector('[data-ecx-status-badge]'),
        statusBadgeText: root.querySelector('[data-ecx-status-badge] span'),
        brand: root.querySelector('[data-ecx-brand]'),
        url: root.querySelector('[data-ecx-url]'),
        metaDivider: root.querySelector('.ecx-meta-divider'),
      };

      this.moneyFormat = root.dataset.moneyFormat || '₡{{ amount_no_decimals_with_comma_separator }}';
      this.currency = root.dataset.currency || 'CRC';
      this.initialProduct = this.parseInitialProduct();
      this.defaults = this.captureDefaults();
      this.onSearchInput = debounce((event) => this.searchProducts(event.target.value));
    }

    init() {
      if (!this.canvas || this.root.dataset.ecxInitialized === 'true') return;
      this.root.dataset.ecxInitialized = 'true';
      this.bindEvents();
      this.setFormat(this.defaults.format, false);
      this.canvas.dataset.background = this.defaults.background;
      this.canvas.dataset.layout = this.defaults.layout;

      if (this.initialProduct) {
        this.setProduct(this.initialProduct, { updateUrl: false, initial: true });
      } else {
        this.renderEmptyState();
      }

      const queryHandle = new URLSearchParams(window.location.search).get('ecx_product');
      if (queryHandle && queryHandle !== this.initialProduct?.handle) {
        this.loadProductByHandle(queryHandle, { updateUrl: false });
      }
    }

    parseInitialProduct() {
      try {
        const parsed = JSON.parse(this.initialProductNode?.textContent || 'null');
        return parsed ? this.normalizeProduct(parsed) : null;
      } catch (_error) {
        return null;
      }
    }

    captureDefaults() {
      return {
        format: this.root.dataset.defaultFormat || 'square',
        background: this.root.dataset.defaultBackground || 'electech',
        layout: this.root.dataset.defaultLayout || 'hero',
        status: this.refs.status?.value || 'auto',
        promo: this.refs.promoInput?.value || '',
        secondary: this.refs.secondaryInput?.value || '',
        features: this.refs.featureInputs.map((input) => input.value.trim()),
        toggles: Object.fromEntries(this.refs.toggles.map((input) => [input.dataset.ecxToggle, input.checked])),
      };
    }

    bindEvents() {
      this.refs.search?.addEventListener('input', this.onSearchInput);
      this.refs.search?.addEventListener('keydown', (event) => this.handleSearchKeydown(event));
      this.refs.searchResults?.addEventListener('click', (event) => this.handleSearchResultClick(event));
      this.refs.searchResults?.addEventListener('keydown', (event) => this.handleResultKeydown(event));
      this.refs.variant?.addEventListener('change', () => this.selectVariant(this.refs.variant.value));
      this.refs.status?.addEventListener('change', () => this.updateStatus());
      this.refs.layout?.addEventListener('change', () => {
        this.canvas.dataset.layout = this.refs.layout.value;
      });
      this.refs.background?.addEventListener('change', () => {
        this.canvas.dataset.background = this.refs.background.value;
      });
      this.refs.formatButtons.forEach((button) => {
        button.addEventListener('click', () => this.setFormat(button.dataset.ecxFormat));
      });
      this.refs.imagePicker?.addEventListener('click', (event) => {
        const button = event.target.closest('[data-ecx-image-option]');
        if (!button) return;
        this.selectImage(Number(button.dataset.imageIndex));
      });
      this.refs.promoInput?.addEventListener('input', () => this.updatePricing());
      this.refs.secondaryInput?.addEventListener('input', () => this.updateSecondary());
      this.refs.featureInputs.forEach((input) => {
        input.addEventListener('input', () => {
          this.state.autoFeatures = false;
          this.renderFeatures(this.refs.featureInputs.map((field) => field.value.trim()).filter(Boolean));
        });
      });
      this.refs.toggles.forEach((input) => input.addEventListener('change', () => this.updateVisibility()));
      this.refs.reset?.addEventListener('click', () => this.reset());
      this.refs.capture?.addEventListener('click', () => this.enterCapture());
      this.refs.captureExit?.addEventListener('click', () => this.exitCapture());

      document.addEventListener('click', (event) => {
        if (!this.root.contains(event.target) || !event.target.closest('.ecx-control-group--product')) {
          this.closeSearchResults();
        }
      });

      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        if (this.state.capture) {
          this.exitCapture();
        } else {
          this.closeSearchResults();
        }
      });
    }

    normalizeProduct(raw) {
      const options = raw.option_names || raw.options || [];
      const optionNames = options.map((option) => (typeof option === 'string' ? option : option?.name)).filter(Boolean);
      const variants = (raw.variants || []).map((variant) => ({
        id: String(variant.id || ''),
        title: cleanText(variant.public_title || variant.title || 'Variante'),
        available: Boolean(variant.available),
        price: Number(variant.price || 0),
        compare_at_price: Number(variant.compare_at_price || 0),
        sku: cleanText(variant.sku || ''),
        options: Array.isArray(variant.options)
          ? variant.options.map(cleanText)
          : [variant.option1, variant.option2, variant.option3].map(cleanText).filter(Boolean),
        featured_image: imageSource(variant.featured_image),
      }));

      const images = (raw.images || []).map(imageSource).filter(Boolean);
      const featured = imageSource(raw.featured_image || raw.featured_media);
      if (featured && !images.some((image) => imageKey(image) === imageKey(featured))) images.unshift(featured);

      return {
        _ecxNormalized: true,
        id: String(raw.id || ''),
        title: cleanText(raw.title || ''),
        handle: cleanText(raw.handle || ''),
        vendor: cleanText(raw.vendor || ''),
        type: cleanText(raw.type || raw.product_type || ''),
        description: String(raw.description || raw.body_html || ''),
        tags: normalizeTags(raw.tags),
        available: Boolean(raw.available),
        url: raw.url || (raw.handle ? `${this.root.dataset.productsRoot || '/products/'}${raw.handle}` : ''),
        featured_image: featured || images[0] || '',
        images,
        optionNames,
        variants,
      };
    }

    setProduct(product, { updateUrl = true, initial = false } = {}) {
      const normalized = product?._ecxNormalized ? product : this.normalizeProduct(product);
      if (!normalized) return;

      this.state.product = normalized;
      this.state.variant = normalized.variants.find((variant) => variant.available) || normalized.variants[0] || null;
      this.state.imageIndex = 0;
      this.state.autoFeatures = !this.defaults.features.some(Boolean);
      this.canvas.classList.remove('is-empty');
      this.updateProductCopy();
      this.rebuildVariantSelect();
      this.rebuildImagePicker();

      if (this.defaults.features.some(Boolean)) {
        this.refs.featureInputs.forEach((input, index) => {
          input.value = this.defaults.features[index] || '';
        });
        this.renderFeatures(this.defaults.features.filter(Boolean));
      } else {
        this.populateAutomaticFeatures();
      }

      if (!initial && !this.defaults.secondary) {
        this.refs.secondaryInput.value = this.summarizeDescription(normalized.description);
      }
      this.updateSecondary();
      this.selectVariant(this.state.variant?.id, { syncImage: true });
      this.refs.search.value = normalized.title;
      this.refs.searchStatus.textContent = 'Producto cargado desde Shopify.';
      this.closeSearchResults();
      if (updateUrl) this.updateProductUrl(normalized.handle);
    }

    updateProductCopy() {
      const product = this.state.product;
      this.refs.title.textContent = product?.title || 'Selecciona un producto';
      this.refs.vendor.textContent = product?.vendor || '';
      this.refs.vendor.hidden = !product?.vendor;
      const length = product?.title?.length || 0;
      this.canvas.classList.toggle('is-long-title', length > 88);
      this.canvas.classList.toggle('is-very-long-title', length > 135);
      const image = product?.featured_image || product?.images?.[0];
      if (image) this.updateHeroImage(image);
    }

    rebuildVariantSelect() {
      const select = this.refs.variant;
      if (!select) return;
      select.replaceChildren();
      const variants = this.state.product?.variants || [];
      if (!variants.length) {
        const option = new Option('Sin variantes disponibles', '');
        select.append(option);
        select.disabled = true;
        return;
      }

      variants.forEach((variant) => {
        const label = `${variant.title || 'Variante'}${variant.available ? '' : ' · Agotada'}`;
        const option = new Option(label, variant.id, false, variant.id === this.state.variant?.id);
        select.append(option);
      });
      select.disabled = variants.length < 2;
    }

    rebuildImagePicker() {
      const picker = this.refs.imagePicker;
      if (!picker) return;
      picker.replaceChildren();
      const images = this.state.product?.images || [];
      this.refs.imagesGroup.hidden = images.length < 2;

      images.slice(0, 10).forEach((source, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `ecx-image-option${index === this.state.imageIndex ? ' is-selected' : ''}`;
        button.dataset.ecxImageOption = '';
        button.dataset.imageIndex = String(index);
        button.setAttribute('aria-label', `Usar imagen ${index + 1}`);
        button.setAttribute('aria-pressed', index === this.state.imageIndex ? 'true' : 'false');
        const image = document.createElement('img');
        image.src = sizedImageUrl(source, 144);
        image.alt = '';
        image.loading = 'lazy';
        image.width = 54;
        image.height = 54;
        button.append(image);
        picker.append(button);
      });
    }

    selectVariant(id, { syncImage = true } = {}) {
      const variants = this.state.product?.variants || [];
      const variant = variants.find((item) => item.id === String(id)) || variants[0] || null;
      this.state.variant = variant;
      if (this.refs.variant && variant) this.refs.variant.value = variant.id;

      if (syncImage && variant?.featured_image) {
        const index = this.state.product.images.findIndex((source) => imageKey(source) === imageKey(variant.featured_image));
        if (index >= 0) this.selectImage(index);
        else this.updateHeroImage(variant.featured_image);
      }

      if (this.state.autoFeatures) this.populateAutomaticFeatures();
      this.updatePricing();
      this.updateStatus();
      this.updateMeta();
      this.updateVisibility();
    }

    selectImage(index) {
      const images = this.state.product?.images || [];
      if (!Number.isFinite(index) || !images[index]) return;
      this.state.imageIndex = index;
      this.updateHeroImage(images[index]);
      [...this.refs.imagePicker.querySelectorAll('[data-ecx-image-option]')].forEach((button) => {
        const selected = Number(button.dataset.imageIndex) === index;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
    }

    updateHeroImage(source) {
      if (!source || !this.refs.productImageShell) return;
      let image = this.refs.productImageShell.querySelector('.ecx-product-image');
      if (!image) {
        this.refs.productImageShell.replaceChildren();
        image = document.createElement('img');
        image.className = 'ecx-product-image';
        image.loading = 'eager';
        image.decoding = 'async';
        this.refs.productImageShell.append(image);
      }

      image.classList.add('is-changing');
      const applySource = () => {
        image.src = sizedImageUrl(source, 2000);
        image.srcset = [480, 720, 960, 1200, 1600, 2000]
          .map((width) => `${sizedImageUrl(source, width)} ${width}w`)
          .join(', ');
        image.sizes = '(min-width: 990px) 58vw, 88vw';
        image.alt = this.state.product?.title || 'Producto';
        window.requestAnimationFrame(() => image.classList.remove('is-changing'));
      };
      window.setTimeout(applySource, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 90);
    }

    updatePricing() {
      const variant = this.state.variant;
      if (!variant) {
        this.refs.price.textContent = '—';
        this.refs.compare.classList.add('is-hidden');
        this.refs.promo.classList.add('is-hidden');
        return;
      }

      const comparePrice = Number(variant.compare_at_price || 0);
      const price = Number(variant.price || 0);
      const discounted = comparePrice > price;
      this.refs.price.textContent = this.formatMoney(price);
      this.refs.compare.textContent = discounted ? this.formatMoney(comparePrice) : '';

      const manualLabel = this.refs.promoInput.value.trim();
      if (manualLabel) {
        this.refs.promo.textContent = manualLabel;
      } else if (discounted) {
        const savings = comparePrice - price;
        const percent = Math.round((savings / comparePrice) * 100);
        this.refs.promo.textContent = `AHORRA ${this.formatMoney(savings)} · -${percent}%`;
      } else {
        this.refs.promo.textContent = '';
      }
      this.updateVisibility();
    }

    updateStatus() {
      const value = this.refs.status?.value || 'auto';
      const variant = this.state.variant;
      let label = '';
      if (value === 'auto' && variant) label = variant.available ? 'Disponible' : 'Agotado';
      else if (value !== 'none') label = STATUS_LABELS[value] || '';

      this.refs.statusBadgeText.textContent = label;
      this.refs.statusBadge.classList.toggle('is-hidden', !label);
      this.refs.statusBadge.classList.toggle('is-unavailable', label === 'Agotado');
      this.refs.statusBadge.dataset.status = value;
    }

    updateMeta() {
      const variant = this.state.variant;
      const status = this.refs.status?.value;
      this.refs.sku.textContent = variant?.sku || '';
      if (!variant) {
        this.refs.availability.textContent = '';
      } else if (status === 'preorder') {
        this.refs.availability.textContent = 'Disponible bajo pedido';
      } else {
        this.refs.availability.textContent = variant.available ? 'Entrega disponible' : 'Consulta disponibilidad';
      }
    }

    updateSecondary() {
      const text = this.refs.secondaryInput?.value.trim() || '';
      this.refs.secondary.textContent = text;
      this.refs.secondary.classList.toggle('is-hidden', !text);
    }

    updateVisibility() {
      const variant = this.state.variant;
      const toggles = Object.fromEntries(this.refs.toggles.map((input) => [input.dataset.ecxToggle, input.checked]));
      const hasCompare = Boolean(variant && Number(variant.compare_at_price) > Number(variant.price));
      const hasPromo = Boolean(this.refs.promo.textContent.trim());
      const hasFeatures = this.refs.features.children.length > 0;
      const hasSku = Boolean(variant?.sku);

      this.refs.compare.classList.toggle('is-hidden', !toggles.compare || !hasCompare);
      this.refs.promo.classList.toggle('is-hidden', !toggles.discount || !hasPromo);
      this.refs.features.classList.toggle('is-hidden', !toggles.features || !hasFeatures);
      this.refs.brand.classList.toggle('is-hidden', !toggles.brand);
      this.refs.url.classList.toggle('is-hidden', !toggles.url);
      this.refs.skuWrap.hidden = !toggles.sku || !hasSku;
      this.refs.metaDivider.classList.toggle('is-hidden', !toggles.sku || !hasSku);
    }

    populateAutomaticFeatures() {
      const features = this.extractFeatures(this.state.product, this.state.variant);
      this.refs.featureInputs.forEach((input, index) => {
        input.value = features[index] || '';
      });
      this.renderFeatures(features);
    }

    extractFeatures(product, variant) {
      if (!product) return [];
      const features = [];
      const pushUnique = (value) => {
        const cleaned = truncateAtWord(value, 58);
        if (!cleaned || /\b(no indicado|no aplica|n\/a)\b/i.test(cleaned)) return;
        if (!features.some((item) => item.toLocaleLowerCase('es') === cleaned.toLocaleLowerCase('es'))) features.push(cleaned);
      };

      (variant?.options || []).forEach((value, index) => {
        const name = product.optionNames[index] || '';
        if (!value || /default title/i.test(value) || /^title$/i.test(name)) return;
        pushUnique(name ? `${name}: ${value}` : value);
      });

      product.tags.forEach((tag) => {
        const match = tag.match(/^(?:feature|caracter[ií]stica|destacado)\s*:\s*(.+)$/i);
        if (match) pushUnique(match[1]);
      });

      const description = stripHtml(product.description);
      description
        .split(/(?:^|\n|\s)\s*[-•]\s+/)
        .map(cleanText)
        .filter((item) => item.length >= 4)
        .forEach((item) => pushUnique(item));

      return features.slice(0, 5);
    }

    renderFeatures(features) {
      this.refs.features.replaceChildren();
      features.slice(0, 5).forEach((feature) => {
        const item = document.createElement('li');
        const text = document.createElement('span');
        text.textContent = feature;
        item.append(text);
        this.refs.features.append(item);
      });
      this.updateVisibility();
    }

    summarizeDescription(description) {
      const text = cleanText(stripHtml(description).replace(/^[-•]\s*/, ''));
      return truncateAtWord(text, 90);
    }

    formatMoney(cents) {
      const numeric = Number(cents || 0);
      const token = this.moneyFormat.match(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/)?.[1] || 'amount_no_decimals_with_comma_separator';
      const noDecimals = String(this.currency).toUpperCase() === 'CRC' || token.includes('no_decimals');
      const commaSeparator = token.includes('comma_separator');
      const amount = numeric / 100;
      const fixed = amount.toFixed(noDecimals ? 0 : 2);
      const [integerPart, decimalPart] = fixed.split('.');
      const thousands = commaSeparator ? '.' : ',';
      const decimal = commaSeparator ? ',' : '.';
      const grouped = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      const formatted = decimalPart ? `${grouped}${decimal}${decimalPart}` : grouped;
      const result = this.moneyFormat.replace(/\{\{\s*[a-zA-Z0-9_]+\s*\}\}/, formatted);
      const decoder = document.createElement('textarea');
      decoder.innerHTML = result;
      return cleanText(decoder.value || `₡${formatted}`);
    }

    setFormat(format, announce = true) {
      const selected = FORMAT_LABELS[format] ? format : 'square';
      this.canvas.dataset.format = selected;
      this.refs.formatButtons.forEach((button) => {
        const active = button.dataset.ecxFormat === selected;
        button.classList.toggle('is-selected', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      this.refs.formatLabel.textContent = FORMAT_LABELS[selected];
      if (announce) this.refs.formatLabel.setAttribute('aria-live', 'polite');
    }

    async searchProducts(query) {
      const term = cleanText(query);
      if (term.length < 2) {
        this.closeSearchResults();
        this.refs.searchStatus.textContent = 'Escribe al menos 2 caracteres para buscar.';
        return;
      }

      this.abortController?.abort();
      this.abortController = new AbortController();
      this.refs.searchBox.classList.add('is-loading');
      this.refs.searchStatus.textContent = 'Buscando en el catálogo…';

      try {
        const rootPath = window.Shopify?.routes?.root || '/';
        const url = new URL(`${rootPath}search/suggest.json`, window.location.origin);
        url.searchParams.set('q', term);
        url.searchParams.set('resources[type]', 'product');
        url.searchParams.set('resources[limit]', '8');
        url.searchParams.set('resources[options][unavailable_products]', 'last');
        url.searchParams.set('resources[options][fields]', 'title,product_type,vendor,variants.title,variants.sku');
        const response = await fetch(url, {
          headers: { Accept: 'application/json' },
          signal: this.abortController.signal,
        });
        if (!response.ok) throw new Error(`Search failed: ${response.status}`);
        const payload = await response.json();
        const products = payload?.resources?.results?.products || [];
        this.renderSearchResults(products);
        this.refs.searchStatus.textContent = products.length
          ? `${products.length} producto${products.length === 1 ? '' : 's'} encontrado${products.length === 1 ? '' : 's'}.`
          : 'No encontramos coincidencias. Prueba otro nombre o SKU.';
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.renderSearchResults([]);
        this.refs.searchStatus.textContent = 'No fue posible buscar ahora. Intenta de nuevo.';
      } finally {
        this.refs.searchBox.classList.remove('is-loading');
      }
    }

    renderSearchResults(products) {
      const container = this.refs.searchResults;
      container.replaceChildren();
      if (!products.length) {
        const empty = document.createElement('p');
        empty.className = 'ecx-search-results__empty';
        empty.textContent = 'Sin resultados';
        container.append(empty);
      } else {
        products.forEach((product) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'ecx-search-result';
          button.role = 'option';
          button.dataset.productUrl = product.url || '';
          button.dataset.productHandle = product.handle || '';

          const source = imageSource(product.image || product.featured_image);
          if (source) {
            const image = document.createElement('img');
            image.src = sizedImageUrl(source, 160);
            image.alt = '';
            image.loading = 'lazy';
            button.append(image);
          } else {
            const placeholder = document.createElement('span');
            placeholder.className = 'ecx-search-result__placeholder';
            placeholder.textContent = 'EC';
            button.append(placeholder);
          }

          const copy = document.createElement('span');
          const title = document.createElement('strong');
          title.textContent = product.title || 'Producto';
          const meta = document.createElement('small');
          meta.textContent = [product.vendor, product.price].filter(Boolean).join(' · ');
          copy.append(title, meta);
          button.append(copy);
          container.append(button);
        });
      }
      container.hidden = false;
      this.refs.search.setAttribute('aria-expanded', 'true');
    }

    handleSearchKeydown(event) {
      if (event.key === 'Escape') {
        this.closeSearchResults();
        return;
      }
      if (event.key !== 'ArrowDown' || this.refs.searchResults.hidden) return;
      const first = this.refs.searchResults.querySelector('.ecx-search-result');
      if (first) {
        event.preventDefault();
        first.focus();
      }
    }

    handleResultKeydown(event) {
      if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
      const options = [...this.refs.searchResults.querySelectorAll('.ecx-search-result')];
      const current = options.indexOf(document.activeElement);
      if (current < 0) return;
      event.preventDefault();
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      options[(current + direction + options.length) % options.length]?.focus();
    }

    handleSearchResultClick(event) {
      const button = event.target.closest('.ecx-search-result');
      if (!button) return;
      if (button.dataset.productHandle) {
        this.loadProductByHandle(button.dataset.productHandle);
      } else if (button.dataset.productUrl) {
        this.loadProductByUrl(button.dataset.productUrl);
      }
    }

    async loadProductByHandle(handle, options = {}) {
      const rootPath = window.Shopify?.routes?.root || '/';
      const url = `${rootPath}products/${encodeURIComponent(handle)}.js`;
      return this.fetchProduct(url, options);
    }

    async loadProductByUrl(productUrl, options = {}) {
      try {
        const url = new URL(productUrl, window.location.origin);
        if (url.origin !== window.location.origin) throw new Error('Unexpected product origin');
        const path = url.pathname.replace(/\/$/, '');
        return this.fetchProduct(`${path}.js`, options);
      } catch (_error) {
        this.refs.searchStatus.textContent = 'No fue posible abrir ese producto.';
        return null;
      }
    }

    async fetchProduct(url, options = {}) {
      this.refs.searchBox.classList.add('is-loading');
      this.refs.searchStatus.textContent = 'Cargando producto…';
      this.canvas.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(url, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(`Product failed: ${response.status}`);
        const product = this.normalizeProduct(await response.json());
        this.setProduct(product, options);
        return product;
      } catch (_error) {
        this.refs.searchStatus.textContent = 'No fue posible cargar el producto. Intenta nuevamente.';
        return null;
      } finally {
        this.refs.searchBox.classList.remove('is-loading');
        this.canvas.removeAttribute('aria-busy');
      }
    }

    closeSearchResults() {
      if (!this.refs.searchResults) return;
      this.refs.searchResults.hidden = true;
      this.refs.search.setAttribute('aria-expanded', 'false');
    }

    updateProductUrl(handle) {
      if (!handle || window.Shopify?.designMode) return;
      const url = new URL(window.location.href);
      url.searchParams.set('ecx_product', handle);
      window.history.replaceState({}, '', url);
    }

    reset() {
      if (this.state.capture) this.exitCapture();
      this.refs.status.value = this.defaults.status;
      this.refs.layout.value = this.defaults.layout;
      this.refs.background.value = this.defaults.background;
      this.refs.promoInput.value = this.defaults.promo;
      this.refs.secondaryInput.value = this.defaults.secondary;
      this.refs.featureInputs.forEach((input, index) => {
        input.value = this.defaults.features[index] || '';
      });
      this.refs.toggles.forEach((input) => {
        input.checked = Boolean(this.defaults.toggles[input.dataset.ecxToggle]);
      });
      this.setFormat(this.defaults.format, false);
      this.canvas.dataset.layout = this.defaults.layout;
      this.canvas.dataset.background = this.defaults.background;
      this.state.autoFeatures = !this.defaults.features.some(Boolean);

      if (this.initialProduct) {
        this.setProduct(this.initialProduct, { updateUrl: false, initial: true });
      } else {
        this.renderEmptyState();
      }
      const url = new URL(window.location.href);
      url.searchParams.delete('ecx_product');
      window.history.replaceState({}, '', url);
    }

    renderEmptyState() {
      this.state.product = null;
      this.state.variant = null;
      this.canvas.classList.add('is-empty');
      this.refs.title.textContent = 'Selecciona un producto';
      this.refs.vendor.hidden = true;
      this.refs.price.textContent = '—';
      this.refs.compare.classList.add('is-hidden');
      this.refs.promo.classList.add('is-hidden');
      this.refs.features.replaceChildren();
      this.refs.statusBadge.classList.add('is-hidden');
      this.refs.skuWrap.hidden = true;
      this.refs.availability.textContent = '';
    }

    enterCapture() {
      if (this.state.capture) return;
      this.state.capture = true;
      this.scrollPosition = { x: window.scrollX, y: window.scrollY };
      this.root.dataset.capture = 'true';
      document.body.classList.add('ecx-capture-active');
      this.refs.capture.setAttribute('aria-pressed', 'true');
      this.hideExternalFixedElements();
      window.requestAnimationFrame(() => {
        this.canvas.scrollIntoView({ block: 'center', inline: 'center' });
      });
    }

    exitCapture() {
      if (!this.state.capture) return;
      this.state.capture = false;
      delete this.root.dataset.capture;
      document.body.classList.remove('ecx-capture-active');
      this.refs.capture.setAttribute('aria-pressed', 'false');
      document.querySelectorAll('[data-ecx-capture-hidden="true"]').forEach((element) => {
        element.removeAttribute('data-ecx-capture-hidden');
      });
      window.requestAnimationFrame(() => window.scrollTo(this.scrollPosition.x, this.scrollPosition.y));
    }

    hideExternalFixedElements() {
      document.querySelectorAll('body *').forEach((element) => {
        if (this.root.contains(element) || element.matches('script, style, link, meta')) return;
        const style = window.getComputedStyle(element);
        const visible = element.getClientRects().length > 0 && style.visibility !== 'hidden';
        if (visible && style.position === 'fixed') element.dataset.ecxCaptureHidden = 'true';
      });
    }

    destroy() {
      this.abortController?.abort();
      if (this.state.capture) this.exitCapture();
      delete this.root.dataset.ecxInitialized;
    }
  }

  const createManager = () => {
    const instances = new WeakMap();
    return {
      init(container = document) {
        const roots = [];
        if (container.matches?.('[data-ecx-studio]')) roots.push(container);
        roots.push(...container.querySelectorAll?.('[data-ecx-studio]') || []);
        roots.forEach((root) => {
          if (instances.has(root)) return;
          const instance = new ShowcaseStudio(root);
          instances.set(root, instance);
          instance.init();
        });
      },
      destroy(container) {
        const root = container.matches?.('[data-ecx-studio]') ? container : container.querySelector?.('[data-ecx-studio]');
        if (!root) return;
        instances.get(root)?.destroy();
        instances.delete(root);
      },
    };
  };

  if (!window[MANAGER_KEY]) {
    window[MANAGER_KEY] = createManager();
    document.addEventListener('shopify:section:load', (event) => window[MANAGER_KEY].init(event.target));
    document.addEventListener('shopify:section:unload', (event) => window[MANAGER_KEY].destroy(event.target));
  }

  const initialize = () => window[MANAGER_KEY].init(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
